import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Building2, Camera, Upload, Tag, Wallet, ChevronLeft, ChevronRight, CheckCircle2, ShieldCheck, Store } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { FileUpload } from "@/components/FileUpload";
import { toast } from "sonner";

export default function BecomeSeller() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [type, setType] = useState("individual");
  const [form, setForm] = useState({ store_name: "", location: "", description: "", id_number: "", full_name: "", legal_entity_name: "", npwp: "" });
  const [docs, setDocs] = useState({});
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setDoc = (k) => (arr) => setDocs({ ...docs, [k]: arr });

  const STEPS = ["Tipe Penjual", "Info Toko", "Verifikasi", "Review"];
  const docsComplete = () => {
    const required = type === "individual" ? ["ktp", "selfie"] : ["npwp_doc", "nib", "ktp"];
    return required.every((r) => docs[r] && docs[r][0]);
  };
  const next = () => {
    if (step === 1 && (!form.store_name.trim() || !form.location.trim()))
      return toast.error("Lengkapi nama toko dan lokasi");
    if (step === 2 && !docsComplete())
      return toast.error("Lengkapi semua dokumen KYC untuk registrasi QRIS");
    setStep((s) => Math.min(s + 1, 3));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return navigate("/login", { state: { from: "/buka-toko" } });
    const required = type === "individual" ? ["ktp", "selfie"] : ["npwp_doc", "nib", "ktp"];
    for (const r of required) {
      if (!docs[r] || !docs[r][0]) { toast.error("Lengkapi semua dokumen KYC untuk registrasi QRIS"); return; }
    }
    const documents = Object.fromEntries(Object.entries(docs).map(([k, v]) => [k, v && v[0]]).filter(([, v]) => v));
    setLoading(true);
    try {
      await api.post("/seller/apply", { seller_type: type, ...form, documents });
      await refresh();
      toast.success("Toko berhasil dibuat!");
      navigate("/seller");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="bg-rl-secondary text-white">
        <div className="max-w-4xl mx-auto px-4 py-14 text-center">
          <h1 className="font-serif text-4xl font-bold">Buka Toko di RELOVA</h1>
          <p className="mt-3 text-white/80">Punya barang yang sudah tidak dipakai? Buka toko dan temukan pemilik barunya.</p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 text-left">
            {[[Camera, "Foto"], [Upload, "Upload"], [Tag, "Jual"], [Wallet, "Terima Hasil"]].map(([Icon, t], i) => (
              <div key={t} className="rounded-xl bg-white/10 p-3 text-sm"><Icon className="w-5 h-5 mb-1" /><span className="font-semibold">0{i + 1} {t}</span></div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="rounded-2xl border border-rl-border bg-white p-6">
          {/* Stepper */}
          <div className="mb-8 flex items-center" data-testid="wizard-stepper">
            {STEPS.map((label, i) => (
              <React.Fragment key={label}>
                {i > 0 && <div className={`h-0.5 flex-1 mx-2 transition-colors ${i <= step ? "bg-rl-primary" : "bg-rl-border"}`} />}
                <button type="button" data-testid={`wizard-step-${i}`} onClick={() => i < step && setStep(i)} className="flex flex-col items-center gap-1 shrink-0">
                  <span className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold transition-colors ${i < step ? "bg-rl-primary text-white" : i === step ? "bg-rl-primary text-white ring-4 ring-rl-primary/15" : "bg-rl-bg2 text-rl-muted"}`}>
                    {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                  </span>
                  <span className={`text-[11px] font-semibold ${i === step ? "text-rl-primary" : "text-rl-muted"}`}>{label}</span>
                </button>
              </React.Fragment>
            ))}
          </div>

          <form onSubmit={submit} onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") e.preventDefault(); }}>
            <div key={step} className="min-h-[260px]">
              {step === 0 && (
                <div>
                  <h2 className="font-bold text-lg text-rl-ink">Pilih Tipe Penjual</h2>
                  <p className="text-sm text-rl-muted mt-1">Menentukan jenis verifikasi yang diperlukan.</p>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <button type="button" data-testid="seller-type-individual" onClick={() => setType("individual")}
                      className={`rounded-xl border p-4 text-left transition-colors ${type === "individual" ? "border-rl-primary bg-rl-primary/5" : "border-rl-border"}`}>
                      <User className="w-5 h-5 text-rl-primary" /><div className="mt-2 font-semibold text-sm">Perorangan</div><div className="text-xs text-rl-muted">Jual barang pribadi</div></button>
                    <button type="button" data-testid="seller-type-business" onClick={() => setType("business")}
                      className={`rounded-xl border p-4 text-left transition-colors ${type === "business" ? "border-rl-primary bg-rl-primary/5" : "border-rl-border"}`}>
                      <Building2 className="w-5 h-5 text-rl-primary" /><div className="mt-2 font-semibold text-sm">Bisnis</div><div className="text-xs text-rl-muted">Thrift / consignment store</div></button>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div>
                  <h2 className="font-bold text-lg text-rl-ink">Informasi Toko</h2>
                  <p className="text-sm text-rl-muted mt-1">Tampil di etalase dan halaman toko kamu.</p>
                  <div className="mt-4 space-y-3">
                    <input data-testid="store-name" value={form.store_name} onChange={set("store_name")} placeholder="Nama toko (mis. Budi's Closet) *" className="w-full rounded-lg border border-rl-border px-3 py-2.5 text-sm outline-none focus:border-rl-primary" />
                    <input data-testid="store-location" value={form.location} onChange={set("location")} placeholder="Lokasi (mis. Jakarta) *" className="w-full rounded-lg border border-rl-border px-3 py-2.5 text-sm outline-none focus:border-rl-primary" />
                    <textarea data-testid="store-desc" value={form.description} onChange={set("description")} placeholder="Deskripsi toko (opsional)" className="w-full rounded-lg border border-rl-border px-3 py-2.5 text-sm outline-none focus:border-rl-primary min-h-[80px]" />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <h2 className="font-bold text-lg text-rl-ink flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-rl-primary" />{type === "individual" ? "Verifikasi Identitas (KYC)" : "Verifikasi Bisnis (KYB)"}</h2>
                  <p className="text-sm text-rl-muted mt-1">Dokumen digunakan untuk verifikasi & registrasi merchant QRIS (PTEN). Pastikan foto jelas dan terbaca.</p>
                  <div className="mt-4 space-y-3">
                    {type === "individual" ? (
                      <>
                        <input data-testid="kyc-fullname" value={form.full_name} onChange={set("full_name")} placeholder="Nama sesuai KTP" className="w-full rounded-lg border border-rl-border px-3 py-2.5 text-sm outline-none focus:border-rl-primary" />
                        <input data-testid="kyc-idnumber" value={form.id_number} onChange={set("id_number")} placeholder="Nomor KTP (NIK)" className="w-full rounded-lg border border-rl-border px-3 py-2.5 text-sm outline-none focus:border-rl-primary" />
                        <div className="grid grid-cols-2 gap-3 pt-1">
                          <FileUpload testid="doc-ktp" label="Foto KTP" value={docs.ktp || []} onChange={setDoc("ktp")} max={1} />
                          <FileUpload testid="doc-selfie" label="Selfie + KTP" value={docs.selfie || []} onChange={setDoc("selfie")} max={1} />
                        </div>
                      </>
                    ) : (
                      <>
                        <input data-testid="kyb-entity" value={form.legal_entity_name} onChange={set("legal_entity_name")} placeholder="Nama badan usaha" className="w-full rounded-lg border border-rl-border px-3 py-2.5 text-sm outline-none focus:border-rl-primary" />
                        <input data-testid="kyb-npwp" value={form.npwp} onChange={set("npwp")} placeholder="NPWP" className="w-full rounded-lg border border-rl-border px-3 py-2.5 text-sm outline-none focus:border-rl-primary" />
                        <div className="grid grid-cols-3 gap-3 pt-1">
                          <FileUpload testid="doc-npwp" label="Foto NPWP" value={docs.npwp_doc || []} onChange={setDoc("npwp_doc")} max={1} />
                          <FileUpload testid="doc-nib" label="NIB / Akta" value={docs.nib || []} onChange={setDoc("nib")} max={1} />
                          <FileUpload testid="doc-ktp" label="KTP Penanggung Jawab" value={docs.ktp || []} onChange={setDoc("ktp")} max={1} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div>
                  <h2 className="font-bold text-lg text-rl-ink">Review & Buka Toko</h2>
                  <p className="text-sm text-rl-muted mt-1">Pastikan data sudah benar sebelum dikirim.</p>
                  <div className="mt-4 space-y-3">
                    <div className="rounded-xl border border-rl-border p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-rl-secondary text-white grid place-items-center shrink-0"><Store className="w-5 h-5" /></div>
                      <div><div className="font-bold text-rl-ink">{form.store_name || "—"}</div>
                        <div className="text-xs text-rl-muted">{form.location || "—"} · {type === "individual" ? "Perorangan" : "Bisnis"}</div></div>
                    </div>
                    <div className="rounded-xl border border-rl-border p-4">
                      <div className="text-xs font-semibold text-rl-muted uppercase mb-2">Dokumen Terunggah ({Object.values(docs).filter((d) => d && d[0]).length})</div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(docs).filter(([, v]) => v && v[0]).map(([k, v]) => (
                          <a key={k} href={v[0]} target="_blank" rel="noreferrer" data-testid={`review-doc-${k}`} className="inline-flex items-center gap-1.5 rounded-lg border border-rl-border px-2.5 py-1.5 text-xs font-semibold text-rl-ink hover:border-rl-primary">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />{k.replace("_", " ").toUpperCase()}</a>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-rl-muted">Verifikasi ditinjau admin. Kamu tetap bisa mulai listing; produk tayang setelah lolos moderasi & KYC disetujui.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Nav */}
            <div className="mt-6 flex gap-3">
              {step > 0 && (
                <button type="button" data-testid="wizard-back" onClick={back}
                  className="inline-flex items-center gap-1 rounded-full border border-rl-border hover:border-rl-primary text-rl-ink font-semibold px-6 py-3 transition-colors">
                  <ChevronLeft className="w-4 h-4" />Kembali</button>
              )}
              {step < 3 ? (
                <button type="button" data-testid="wizard-next" onClick={next}
                  className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-rl-primary hover:bg-rl-primaryHover text-white font-bold py-3 transition-colors">
                  Lanjut<ChevronRight className="w-4 h-4" /></button>
              ) : (
                <button data-testid="submit-become-seller" type="submit" disabled={loading}
                  className="flex-1 rounded-full bg-rl-primary hover:bg-rl-primaryHover text-white font-bold py-3 transition-colors disabled:opacity-50">
                  {loading ? "Memproses..." : "BUKA TOKO SEKARANG"}</button>
              )}
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
